---
title: "The Yelp API in 2026: what it costs now, and what else returns the same data"
description: "Yelp's API now starts at $229 a month, with a trial that's for evaluation only. We ran 20 Yelp searches and priced 6 ways to get Yelp data from your code."
date: "2026-10-25"
postType: "alternatives"
author: "Kevin Wang"
tags: ["yelp", "local-business", "reviews", "api", "comparison"]
keyword: "yelp api"
ogImage: "/og/yelp-api.png"
---

export const faqItems = [
  {
    q: "Is the Yelp API free?",
    a: "No. On October 2, 2026, Yelp's Places API had a 30-day trial with 5,000 free calls, which Yelp's FAQ says are strictly for evaluation and not for commercial deployment. After that, the plans are Base at $229 a month, Enhanced at $299, and Premium at $643, each including 30,000 calls a month.",
  },
  {
    q: "How much does the Yelp API cost?",
    a: "Yelp's Places API costs $229 a month on the Base plan, $299 on Enhanced, and $643 on Premium, each with 30,000 calls a month and a daily limit of up to 5,000. Calls past 30,000 cost $5.91, $6.57, or $14.13 per 1,000. Yelp's Insights API, the one licensed for analysis, starts at $0.04 per unique location on a rolling 12 months. Prices from Yelp's pages on October 2, 2026.",
  },
  {
    q: "Can I get full Yelp reviews from the API?",
    a: "Not from the Places API. Yelp's FAQ says it doesn't return full review text: the Enhanced plan gets up to 3 review excerpts of about 160 characters, Premium up to 7, and Base none. Full review text is listed on the Premium tier of Yelp's Insights API. SerpApi's Yelp Reviews API and Apify's Yelp Reviews Scraper return full review text without a Yelp contract.",
  },
  {
    q: "Can I store data from the Yelp API?",
    a: "Only briefly. Yelp's FAQ says you may cache Places API content for at most 24 hours and keep Yelp business IDs indefinitely. Its API terms also say you can't use the content to create your own database of business listings, and its FAQ says analysis isn't permitted for Places integrations.",
  },
];

<Callout type="tip" title="Key findings">

- **Yelp's API isn't free.** The [Places API](https://business.yelp.com/data/products/places-api/) starts at $229 a month for 30,000 calls, and its 30-day trial is "strictly for evaluation and not for commercial deployment".
- **Places is licensed for showing Yelp in your own app, not for analysis.** Yelp's FAQ says "Analysis is not permitted", content can be cached for 24 hours, and review text is capped at 3 excerpts (7 on Premium). Analysis is a separate product, the Insights API, priced per location.
- **To get Yelp search results from code without a Yelp plan, use a hosted API.** [AnyAPI](https://getanyapi.com/api/yelp/search) returned 20 listings on all 20 searches tested, with a phone number on 377 of 400 and the unrounded star rating on all of them. [SerpApi](https://serpapi.com/yelp-search-api) is the pick if you already use it for Google.
- **For full review text, skip Places.** SerpApi's [Yelp Reviews API](https://serpapi.com/yelp-reviews-api) returns up to 49 full reviews a search, and Apify's [Yelp Reviews Scraper](https://apify.com/delicious_zebu/yelp-reviews-scraper) bills $1.00 per 1,000 reviews with no plan.

</Callout>

You've got a list of plumbers in Austin, or every taco place in Los Angeles, and you want their Yelp rating, review count, and phone number next to each one. Yelp has an API for that.

It has a monthly price, a trial you can't ship on, and terms that decide what you're allowed to build. I read Yelp's developer docs and pricing pages, ran 20 local searches through AnyAPI's Yelp endpoint on October 2, 2026, and priced the rest from their own pages.

Here's what each option is for.

## The best Yelp APIs

- [Yelp Places API](#best-yelp-api-for-showing-yelp-content-in-a-consumer-app) for showing Yelp content in a consumer app
- [Yelp Insights API](#best-yelp-api-for-licensed-analysis-at-scale) for licensed analysis at scale
- [AnyAPI](#best-yelp-api-for-search-results-billed-per-call) for search results billed per call
- [SerpApi Yelp APIs](#best-yelp-api-for-yelp-results-next-to-google) for Yelp results next to Google
- [Apify Yelp Scraper](#best-yelp-api-for-business-details-like-hours-and-websites) for business details like hours and websites
- [Apify Yelp Reviews Scraper](#best-yelp-api-for-full-review-text) for full review text

## What makes a good Yelp API?

I'm assuming you're building a local-business tool: a lead list, a review tracker, a "best of" page, or a feature that puts ratings next to addresses. You search Yelp by keyword and city, and sometimes you need the reviews behind a rating. Here's what I compared.

- **What you're allowed to do with the data.** Yelp licenses display and analysis as two separate products, so the terms matter as much as the fields.
- **The fields a listing needs.** Name, rating, review count, phone, address, and coordinates, on every row.
- **Reviews.** A rating is one number. The text behind it is a separate request, and on Yelp's own API a dearer plan.
- **Price at your volume.** Every price below is for 10,000 searches a month, on monthly billing.

<Callout type="note" title="How this was tested, and its limits">

- AnyAPI: 20 searches, from "pizza" in Chicago to "moving company" in San Diego, each sent once to `yelp.search` with a limit of 20. That's 20 calls, one at a time from one machine, on 2026-10-02.
- Listings weren't checked against Yelp's own pages, and speeds are from 20 calls, so they're a rough guide, not a benchmark.
- Yelp's APIs weren't called. The Places trial needs a business email and a Yelp app, and Insights is sold through Yelp's sales team, so their sections are read from Yelp's own pages on 2026-10-02.
- SerpApi and the two Apify actors weren't run. Their sections are from their own pages on 2026-10-02.
- Prices are for 10,000 searches a month on monthly billing, checked on 2026-10-02.

</Callout>

## The best Yelp APIs at a glance

| Tool | Best for | Why pick it | Pricing at 10,000 searches a month |
| --- | --- | --- | --- |
| Yelp Places API | Showing Yelp content in a consumer app | Yelp's own search, details, and review excerpts | $229 (Base plan, 30,000 calls included) |
| Yelp Insights API | Licensed analysis at scale | Yelp's data with rights for internal analysis | From $0.04 per unique location, rolling 12 months |
| AnyAPI | Search results billed per call | Up to 20 listings a call with phone and unrounded rating | From <Price sku="yelp.search" calls={10000} /> (<Price sku="yelp.search" /> to <Price sku="yelp.search" failover /> per 1,000) |
| SerpApi Yelp APIs | Yelp results next to Google | Yelp search and full reviews on a search-engine key | $150 (Production plan, 15,000 searches, 10 results each) |
| Apify Yelp Scraper | Business details like hours and websites | Hours, website, amenities, and photos per business | $5.00 per 1,000 businesses on the Free plan, $1.50 on Business |
| Apify Yelp Reviews Scraper | Full review text | Every review, with star and date filters | $1.00 per 1,000 reviews on the Free plan, $0.50 on Business |

## Best Yelp API for showing Yelp content in a consumer app

### Yelp Places API

<Figure src="/og/screenshots/yelp-api-yelp-places-pricing.png" alt="Yelp's Places API pricing: Premium, Enhanced, and Base plans, each with 5,000 free API calls during a 30-day trial. Premium has premium attributes, up to 12 photos and up to 7 review excerpts per business, $643 per month plus $14.13 per additional 1,000 calls. Enhanced is limited to 3 photos and 3 review excerpts, $299 per month plus $6.57. Base has no review excerpts and no photos, $229 per month plus $5.91" caption="Yelp's Places API plans on October 2, 2026: $229 to $643 a month, and review excerpts only from the middle tier up." width={1280} height={900} />

**Yelp Places API pros:**

- Yelp's own data, with search by keyword, category, price level, and distance, plus phone search and business matching
- Up to 50 businesses per search call, and up to 240 per search across pages
- 30,000 calls a month on every paid plan, so 10,000 searches fit with room to spare

**Yelp Places API cons:**

- $229 a month before the first production call, and the trial is evaluation only
- Analysis isn't permitted, and content can be cached for 24 hours at most
- No full review text on any plan: 3 excerpts on Enhanced, 7 on Premium, none on Base

If you're putting Yelp ratings and photos into a consumer product, this is the licensed route, and it's what Yelp built the API for. Yelp's [pricing page](https://business.yelp.com/data/products/places-api/) says a Places license "allows you to access and present Yelp content on your consumer-facing product". The endpoints live under `https://api.yelp.com/v3`, with a private API key from your Yelp app.

You start with a 30-day trial of 5,000 calls, and signing up needs a business email. Yelp's [FAQ](https://docs.developer.yelp.com/docs/places-faq) is blunt about what the trial is for: those calls are "STRICTLY FOR EVALUATION AND NOT FOR COMMERCIAL DEPLOYMENT". Paid plans include 30,000 calls a month, with a daily limit of up to 5,000, and a request past the daily limit gets a 429 until midnight UTC.

<Figure src="/og/screenshots/yelp-api-yelp-faq-analysis.png" alt="Yelp's Places API FAQ: You may cache Yelp Places API content for a maximum of 24 hours. Yelp Business IDs can be stored indefinitely. Analysis is not permitted for Yelp Places integrations. The Yelp Places API does not return full review text. Three review excerpts of approximately 160 characters each are returned by default" caption="Yelp's Places API FAQ on October 2, 2026: 24-hour caching, no analysis, and review excerpts of about 160 characters." width={1280} height={900} />

**The terms are the part to read before the price.** The same FAQ says "Analysis is not permitted for Yelp Places integrations", and you may cache content "for a maximum of 24 hours". Yelp's [API Terms of Use](https://terms.yelp.com/developers/api_terms/20260922_en_us/), updated September 22, 2026, add that you can't use the content "to update or create your own database of business listing information".

So a map in your app that shows Yelp's rating for the restaurant on screen is what Places is for. A spreadsheet of every dentist in Denver, kept and sorted by rating, isn't.

Reviews are thin on every plan. Yelp's [reviews endpoint](https://docs.developer.yelp.com/reference/v3_business_reviews) "returns up to three review excerpts", the FAQ puts them at about 160 characters each, and you can't choose which ones. Base doesn't get them at all.

**Yelp Places API pricing:** a 30-day trial with 5,000 calls for evaluation. Base is $229 a month, Enhanced $299, and Premium $643, each with 30,000 calls; past that, $5.91, $6.57, or $14.13 per 1,000. Over 150,000 calls a month goes to Yelp's sales team. Source: [Yelp Places API](https://business.yelp.com/data/products/places-api/), October 2, 2026.

## Best Yelp API for licensed analysis at scale

### Yelp Insights API

<Figure src="/og/screenshots/yelp-api-yelp-insights-pricing.png" alt="Yelp's Insights API pricing: A Yelp Insights API license gives you access to Yelp data for B2B purposes and internal analysis, priced per unique location on a rolling 12 month basis. Premium adds business viability score, consumer intent signals, photos, full review text, and location popularity score, starting at $0.08 per location. Enhanced adds historical star ratings, business summaries, popular items and pricing, and parent company info, starting at $0.06. Base has business name, address, open or close status, and hours, starting at $0.04" caption="Yelp's Insights API plans on October 2, 2026: priced per location, with full review text on Premium." width={1280} height={900} />

**Yelp Insights API pros:**

- Licensed for "B2B purposes and internal analysis", which Places isn't
- Historical star ratings, hours, open or closed status, and parent company on the higher tiers
- Full review text on Premium

**Yelp Insights API cons:**

- Sold through Yelp's sales team, with a "Talk to us" form rather than a sign-up
- Priced per unique location over a rolling 12 months, so it fits a fixed list of places better than open-ended searching

If you're an insurer, a lender, or a market-research team that wants to analyze Yelp data, this is the product Yelp points you to. The Places FAQ answer on analysis links straight to it.

Yelp's [Insights page](https://business.yelp.com/data/products/insights-api/) prices it per unique location, starting at $0.04 on Base, $0.06 on Enhanced, and $0.08 on Premium, "plus volume based discounts as usage grows". Covering 5,000 locations for a year would start at $200 on Base, if Yelp's starting price holds at your volume.

Skip it if you don't know your list of places yet, or if you're one developer with a side project. It's a sales conversation, and Yelp's page doesn't say how long it takes.

**Yelp Insights API pricing:** starting at $0.04, $0.06, or $0.08 per unique location on a rolling 12 months, with volume discounts. Source: [Yelp Insights API](https://business.yelp.com/data/products/insights-api/), October 2, 2026.

## Best Yelp API for search results billed per call

### AnyAPI

<Figure src="/og/charts/yelp-api-review-counts.svg" alt="Bar chart of the median Yelp review count of the 20 listings returned per search on October 2, 2026. Pizza, Chicago 1,075; Tacos, Los Angeles 824; Sushi, San Francisco 801; Barbecue, Kansas City 680; Vegan restaurant, Nashville 609; Pho, Houston 419; Bagels, Brooklyn 300; HVAC, Las Vegas 298; Ramen, Boston 263; Bakery, Philadelphia 215; Coffee, Seattle 169; Moving company, San Diego 158; Plumber, Austin 153; Hair salon, Atlanta 132; Auto repair, Phoenix 118; Dog groomer, Portland 64; Dentist, Denver 63; Electrician, Columbus 12; Yoga studio, Minneapolis 9; Roofing, Tampa 5" caption="One search call per query, 20 listings each, October 2, 2026. Restaurants run to hundreds of reviews; roofers in Tampa had a median of 5." width={760} height={930} />

**AnyAPI pros:**

- 20 listings on all 20 searches, with rating, review count, coordinates, categories, and a Yelp URL on all 400
- The unrounded star rating next to the half-star one Yelp shows
- Billed per call from a prepaid wallet, with no monthly plan

**AnyAPI cons:**

- Search only: up to 20 listings per call, with no paging and no review text
- No hours or website in the listing

[AnyAPI](https://getanyapi.com/api/yelp) is the pick if you need Yelp search results from code and don't want a $229 plan or a sales call. You send a search term and a city to `yelp.search`, and the listings come back as the same JSON whatever you search for.

```bash
curl -sS -X POST 'https://api.getanyapi.com/v1/run/yelp.search' \
  -H "Authorization: Bearer $ANYAPI_API_KEY" \
  -H 'Content-Type: application/json' \
  -d '{"query":"pizza","location":"Chicago, IL","limit":20}'
```

Here's one of the 20 listings it returned on 2026-10-02, trimmed, with the rest of the envelope:

```json
{
  "output": {
    "found": true,
    "data": {
      "items": [
        {
          "id": "DXwSYgiXqIVNdO9dazel6w",
          "name": "Pequod's Pizza",
          "url": "https://www.yelp.com/biz/pequods-pizza-chicago-3",
          "avg_rating": 4,
          "unrounded_avg_rating": 3.9,
          "review_count": 9008,
          "localized_price": "$$",
          "categories": [{ "name": "Pizza" }],
          "address1": "2207 N Clybourn Ave",
          "city": "Chicago",
          "state": "IL",
          "zip": "60614",
          "neighborhoods": ["DePaul", "Sheffield Neighbors"],
          "dialable_phone": "+17733271512",
          "latitude": 41.92187,
          "longitude": -87.664486,
          "photo_count": 6150,
          "is_closed": false
        }
      ]
    }
  },
  "items": 20
}
```

All 20 searches came back with 20 listings, 400 different businesses in all, in a median of 4.7 seconds. {/* receipt 2026-10-02 */}

| Field | Listings with it, of 400 |
| --- | --- |
| Name, Yelp ID, URL, rating, review count, categories, coordinates | 400 |
| Phone number | 377 |
| Street address | 357 |
| Neighborhood | 303 |
| Price level (`$` to `$$$$`) | 178 |

**The unrounded rating is the field to sort by.** Yelp shows ratings in half stars, and 287 of the 400 listings had an unrounded rating that differed from the rounded one. Pequod's and Giordano's both show 4 stars in Chicago, at 3.9 and 3.8.

**Review counts depend on the trade, so set your filters per category.** The median listing had 169 reviews, but the chart above shows the spread: Chicago pizza places had a median of about 1,075, and Tampa roofers 5. Eight listings, all plumbers, electricians, auto shops, and roofers, had no reviews yet and a rating of 0. A "4 stars and 50 reviews" filter that works for restaurants would empty a roofing list.

Price level is a restaurant and salon field. It was on 178 listings, and on none of the 180 from the nine trade and service searches, from plumbers to movers.

**AnyAPI pricing:** from <Price sku="yelp.search" /> per 1,000 search calls, up to 20 listings each. A call the cheapest source can't answer goes to a backup, which costs up to <Price sku="yelp.search" failover /> per 1,000. Prepaid, no plan; new accounts get free credit, no card required.

## Best Yelp API for Yelp results next to Google

### SerpApi Yelp APIs

<Figure src="/og/screenshots/yelp-api-serpapi-yelp-pricing.png" alt="SerpApi's pricing: Starter $25 a month for 1,000 searches, Developer $75 for 5,000, Production $150 for 15,000, Big Data $275 for 30,000, and a Free plan of 250 searches a month" caption="SerpApi's plans on October 2, 2026: one search count shared across every engine, Yelp included." width={1280} height={900} />

**SerpApi Yelp APIs pros:**

- A Yelp Search API and a Yelp Reviews API, on the same key as Google and other engines
- Up to 49 full reviews per Reviews API request, with a language filter
- A repeated search within an hour is served from cache and isn't counted

**SerpApi Yelp APIs cons:**

- 10 businesses per search page, so 20 listings take two searches
- A monthly plan: 10,000 searches needs the $150 plan

SerpApi returns Yelp's search page as JSON through its [Yelp Search API](https://serpapi.com/yelp-search-api), with `find_desc` for the search term and `find_loc` for the place. Its [Yelp Reviews API](https://serpapi.com/yelp-reviews-api) takes a business ID from a search and returns up to 49 reviews a request, with the full text.

If you already pay SerpApi for Google results, it's the easiest way to add Yelp, and the only option here that returns search results and full reviews on one key. A `yelp_domain` parameter covers Yelp's other country sites.

Skip it if your volume is uneven. Every Yelp page counts as a search against a monthly plan, and a search for 20 businesses costs two.

**SerpApi Yelp APIs pricing:** 250 free searches a month. Starter is $25 a month for 1,000 searches, Developer $75 for 5,000, Production $150 for 15,000, and Big Data $275 for 30,000. Source: [SerpApi pricing](https://serpapi.com/pricing), October 2, 2026.

## Best Yelp API for business details like hours and websites

### Apify Yelp Scraper

<Figure src="/og/screenshots/yelp-api-apify-yelp-scraper-pricing.png" alt="The Apify Store pricing tab for Yelp Scraper by Tri-angle, maintained by Apify: Business, a single business in the default dataset, $5.00 per 1,000 on the Free plan and $1.50 per 1,000 on Business with a 44% Gold discount; additional photo page $1.00 and $0.85 per 1,000; Actor start $0.001. 7K total users and 169 monthly active users" caption="Yelp Scraper's Apify Store pricing on October 2, 2026: $5.00 per 1,000 businesses on Apify's Free plan, $1.50 on Business." width={1280} height={900} />

**Apify Yelp Scraper pros:**

- Opening hours, website, amenities, photos, and health inspection scores where Yelp shows one
- Several search terms across several cities in one run
- Maintained by Apify, and about 7,000 total users on October 2, 2026

**Apify Yelp Scraper cons:**

- Billed per business, so a 20-listing search costs 20 times a 1-listing one
- A run is a batch job that writes to a dataset, so your code waits for the run to finish
- No review text; its own docs send you to a separate review scraper

Tri-angle's [Yelp Scraper](https://apify.com/tri_angle/yelp-scraper) on Apify takes search terms and locations, runs every term in every city, and writes each business to a dataset. Its README lists the fields a lead list wants and a search listing doesn't carry: website, hours, amenities, and whether the business is claimed.

It suits a weekly export of a few thousand businesses into a spreadsheet or CRM. At Apify's Free-plan rate, $5.00 per 1,000 businesses, a 20-business search costs $0.10, so 10,000 of them would be $1,000 a month; at the Business rate, $300.

**Apify Yelp Scraper pricing:** $5.00 per 1,000 businesses on Apify's Free plan and $1.50 on Business, plus $0.001 per run start, and extra for photos past the first 20. Source: [Apify Store](https://apify.com/tri_angle/yelp-scraper/pricing), October 2, 2026.

## Best Yelp API for full review text

### Apify Yelp Reviews Scraper

<Figure src="/og/screenshots/yelp-api-apify-yelp-reviews-pricing.png" alt="The Apify Store pricing tab for Yelp Reviews Scraper by Zebu Data: Result, a single result in the default dataset, $1.00 per 1,000 on the Free plan and $0.50 per 1,000 on Business with a 50% Gold discount; Actor start $0.00005. Rating 4.7 from 33 reviews, 503 total users and 49 monthly active users" caption="Yelp Reviews Scraper's Apify Store pricing on October 2, 2026: $1.00 per 1,000 reviews on Apify's Free plan." width={1280} height={900} />

**Apify Yelp Reviews Scraper pros:**

- Full review text, with the reviewer, date, photos, votes, and the owner's reply
- Filters by star rating and date, and sorting by newest, oldest, rating, or Elite
- A star breakdown for the business in the output

**Apify Yelp Reviews Scraper cons:**

- Takes a Yelp business URL, so you need a search step first
- A community-maintained actor, with about 500 total users on October 2, 2026

If you need what customers actually wrote and don't want a monthly plan, this is the pay-per-review route. Zebu Data's [Yelp Reviews Scraper](https://apify.com/delicious_zebu/yelp-reviews-scraper) takes a business URL, like the `url` field from any search above, and writes one row per review.

It suits a review-tracking job: every 1-star review since last month for 200 locations, say. At $1.00 per 1,000 reviews, pulling 100 reviews for each of 200 businesses is $20 on Apify's Free plan. If you pull reviews every day at volume, SerpApi's 49 reviews a search can work out cheaper on a monthly plan.

**Apify Yelp Reviews Scraper pricing:** $1.00 per 1,000 reviews on Apify's Free plan and $0.50 on Business, plus $0.00005 per run start. Source: [Apify Store](https://apify.com/delicious_zebu/yelp-reviews-scraper/pricing), October 2, 2026.

## Which Yelp API should you use?

- You're showing Yelp ratings and photos inside a consumer app: the Yelp Places API.
- You want to analyze Yelp data under a license, for a known list of places: the Yelp Insights API.
- You need Yelp search results from code, pay as you go, with phone and unrounded rating: AnyAPI.
- You already use SerpApi for Google and want Yelp search and reviews too: SerpApi.
- You want hours, websites, and amenities for a batch export: Apify's Yelp Scraper.
- You need the full text of reviews: Apify's Yelp Reviews Scraper.

Whichever you pick, sort by the unrounded rating and set review-count filters per category. The [Yelp API page](/api/yelp) lists AnyAPI's Yelp endpoint, and the [Google Maps API pricing guide](/blog/google-maps-api-pricing) covers the other place most local-business lists start.

<FAQ items={faqItems} />

<CTA to="/api/yelp/search" note={<>Yelp search results as clean JSON, from <Price sku="yelp.search" per="call" /> for up to 20 listings, no plan and nothing that expires. New accounts get free credit, no card required.</>}>
  Try the Yelp Search API
</CTA>

Use data responsibly and follow AnyAPI's [Acceptable Use Policy](/acceptable-use).
